import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import {
  PiBriefcaseLight,
  PiCoinsLight,
  PiFilePdfLight,
  PiClockLight,
  PiGaugeLight,
  PiHourglassLight,
} from 'react-icons/pi';
import { useSelector } from 'react-redux';
import { useAppDispatch, RootState } from '../store';
import { isMatterEconomicsEnabled } from '../utils/config';
import { fetchFirmEconomics } from '../store/actions/economicsActions';
import { showFeedbackModal } from '../store/actions/UserFeedbackActions';
import { downloadEconomicsReport } from '../utils/reportsApi';
import { formatCurrency } from '../utils/formatCurrency';
import Header from '../components/shared/TailwindComponents/Header';
import Skeleton from '../components/shared/TailwindComponents/Skeleton';
import Spinner from '../components/shared/TailwindComponents/Spinner';
import Button from '../components/shared/TailwindComponents/Button';
import GlassCard from '../components/shared/brand/GlassCard';
import { KpiTile } from '../components/shared/brand/Field';
import FirmEconomicsFilterBar from '../components/Economics/FirmEconomicsFilterBar';
import {
  TopMattersWidget,
  TopClientsWidget,
  HoursByUserWidget,
  RevenueTrendWidget,
} from '../components/Economics/firmEconomicsWidgets';
import type { FirmEconomics, FirmEconomicsFilters } from '../types/economics';

/**
 * Firm economics dashboard (US #2239). Filter bar (#2279) drives four widgets
 * (#2278); matter rows drill into the per-matter Economics tab and user rows
 * filter the dashboard (#2280). Route + sidebar item are flag-gated (#2281).
 *
 * A dashboard, deliberately NOT bound to DetailPageSurface: there is no record
 * here to dock context panes around - it is a filter bar over a KPI strip and
 * four glass widgets.
 */
const FirmEconomicsDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  /*
    GATED TWICE, which is the house pattern (see `FirmAIUsageSection`).

    The nav entry carries `ADMIN_ONLY` so the sidebar never offers this to a
    member - but a bookmark, a pasted link or a typed URL does not go through
    the sidebar. Without this check the whole firm's revenue and every
    person's billed hours rendered for anyone who knew the path.
  */
  const isOrgAdmin = useSelector(
    (state: RootState) => state.user.userInfo?.is_org_admin === true
  );
  const [filters, setFilters] = useState<FirmEconomicsFilters>({});
  const [data, setData] = useState<FirmEconomics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    /* Not authorised is not a fetch. Asking anyway is a 403 in the network
       log and a question in a pen test report. */
    if (!isOrgAdmin) return;
    setLoading(true);
    setError(false);
    setForbidden(false);
    try {
      const res = await dispatch(fetchFirmEconomics(filters));
      setData(res || null);
    } catch (e: any) {
      if (e?.response?.status === 403) setForbidden(true);
      else setError(true);
    } finally {
      setLoading(false);
    }
  }, [dispatch, filters, isOrgAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = data?.totals || {};
  const currency = filters.currency || totals.by_currency?.[0]?.currency || 'eur';
  const money = (v?: number | null) => formatCurrency(Number(v || 0), currency);
  const byCurrency = totals.by_currency || [];
  const isMixed = totals.revenue == null && byCurrency.length > 1;
  // Single-currency total, or "Mixed currencies" (each currency then gets its
  // own tile in the by-currency strip below) when the BE returns a null total.
  let revenueValue: React.ReactNode;
  if (totals.revenue != null) {
    revenueValue = money(totals.revenue);
  } else if (isMixed) {
    revenueValue = (
      <span title={byCurrency.map((c) => formatCurrency(c.revenue, c.currency)).join(' · ')}>
        {t('Mixed currencies')}
      </span>
    );
  } else if (byCurrency.length === 1) {
    revenueValue = formatCurrency(byCurrency[0].revenue, byCurrency[0].currency);
  } else {
    revenueValue = money(0);
  }

  const totalHours = Number(totals.total_hours || 0);
  const billableHours = Number(totals.billable_hours || 0);
  // Firm-wide utilization: the share of recorded time that is billable. Only
  // meaningful once there is time to divide by.
  const utilization = totalHours > 0 ? Math.round((billableHours / totalHours) * 100) : null;

  const onUserClick = (userId: number) => setFilters((p) => ({ ...p, user_id: [userId] }));

  const widgets = useMemo(() => ({
    matters: data?.top_matters || [],
    clients: data?.per_client || [],
    users: data?.per_user || [],
    trend: data?.revenue_trend || [],
  }), [data]);

  // Hooks stay above the members' early return (rules of hooks).
  const [exporting, setExporting] = useState(false);
  const downloadReport = useCallback(async () => {
    setExporting(true);
    try {
      await downloadEconomicsReport({ date_from: filters.date_from, date_to: filters.date_to }, t);
    } catch {
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'error', duration: 5000, message: t('Could not create the report.'),
      }));
    } finally {
      setExporting(false);
    }
  }, [dispatch, filters.date_from, filters.date_to, t]);

  /*
    A MEMBER SEES THE SAME REFUSAL A 403 PRODUCES, not a blank page and not a
    redirect that looks like the route is broken. Rendered before anything
    else, so no filter bar or KPI strip flashes on the way.
  */
  if (!isOrgAdmin) {
    return (
      <div className="h-full flex items-center justify-center p-8" data-testid="economics-not-admin">
        <div className="max-w-sm text-center space-y-2">
          <p className="text-sm font-medium text-fg-strong">
            {t('Firm economics is for organization admins')}
          </p>
          <p className="text-xs text-fg-muted leading-relaxed">
            {t('Your own time and economics are on My time. Ask an admin if you need the firm-wide view.')}
          </p>
        </div>
      </div>
    );
  }

  const renderMessage = () => {
    if (forbidden) {
      return (
        <div className="text-sm text-fg-muted py-10 text-center">
          {t('You do not have access to firm economics.')}
        </div>
      );
    }
    if (error) {
      return (
        <div className="py-10 text-center space-y-3">
          <p className="text-sm text-fg-muted">{t('Could not load firm economics.')}</p>
          <div className="flex justify-center">
            <Button variant="outline" size="small" onClick={load}>{t('Retry')}</Button>
          </div>
        </div>
      );
    }
    return null;
  };


  if (!isMatterEconomicsEnabled()) return <Redirect to="/home" />;

  // First load gets skeletons; a refilter keeps the current numbers on screen
  // behind a plain spinner (never the logo - this is not an AI process).
  const initialLoading = loading && !data;
  const refiltering = loading && !!data;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header
        breadcrumbs={[{ name: t('Firm economics'), href: '/economics', current: true }]}
        actions={!forbidden ? [{
          // F-REPORTS: the same figures as a branded PDF, for the period in the filters.
          label: exporting ? t('Preparing report…') : t('Download report (PDF)'),
          onClick: downloadReport,
          icon: <PiFilePdfLight className="h-4 w-4" />,
          variant: 'outline' as const,
          disabled: exporting,
        }] : []}
      />

      <div className="px-4 py-3 border-b border-hairline">
        <FirmEconomicsFilterBar value={filters} onChange={setFilters} />
      </div>

      <div className="flex-grow overflow-y-auto custom-scrollbar-main px-4 py-3 space-y-4">
        {renderMessage()}

        {!forbidden && !error && (initialLoading ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="kpi" />)}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="card" />)}
            </div>
          </>
        ) : (
          <>
            {refiltering && (
              <div className="flex items-center justify-end gap-2 text-xs text-fg-muted">
                <Spinner size="inline" ariaLabel={t('Updating')} />
                {t('Updating')}
              </div>
            )}

            <div className={refiltering ? 'space-y-4 opacity-60 transition-opacity' : 'space-y-4'}>
              {/* Totals */}
              <GlassCard padding="none">
                <div className="grid grid-cols-2 lg:grid-cols-5 divide-x divide-y lg:divide-y-0 divide-hairline-soft">
                  <KpiTile icon={<PiCoinsLight />} label={t('Revenue')} value={revenueValue} />
                  <KpiTile icon={<PiClockLight />} label={t('Total hours')} value={`${totalHours.toFixed(1)} ${t('h')}`} />
                  <KpiTile icon={<PiHourglassLight />} label={t('Billable hours')} value={`${billableHours.toFixed(1)} ${t('h')}`} />
                  <KpiTile icon={<PiGaugeLight />} label={t('Utilization')} value={utilization != null ? `${utilization}%` : '—'} />
                  <KpiTile
                    icon={<PiBriefcaseLight />}
                    label={t('Matters')}
                    value={String(totals.matter_count ?? widgets.matters.length)}
                  />
                </div>
              </GlassCard>

              {/* Revenue per currency - the detail the single Revenue tile cannot
                  carry once the firm bills in more than one. */}
              {byCurrency.length > 1 && (
                <GlassCard padding="none" title={t('Revenue by currency')}>
                  <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-hairline-soft">
                    {byCurrency.map((c) => (
                      <KpiTile
                        key={c.currency}
                        label={String(c.currency || '').toUpperCase()}
                        value={formatCurrency(c.revenue, c.currency)}
                      />
                    ))}
                  </div>
                </GlassCard>
              )}

              {/* Widgets */}
              {/* The trend runs full width as a short strip, so its height is
                  set by the chart rather than stretched to match a list
                  beside it; the tables size to their rows below it. */}
              <RevenueTrendWidget trend={widgets.trend} currency={currency} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                <TopMattersWidget matters={widgets.matters} currency={currency} />
                <TopClientsWidget clients={widgets.clients} currency={currency} />
              </div>
              <HoursByUserWidget users={widgets.users} currency={currency} onUserClick={onUserClick} />
            </div>
          </>
        ))}
      </div>
    </div>
  );
};

export default FirmEconomicsDashboardPage;
